/**
 * Quais parâmetros de URL a chave de cache do edge descarta — e, pela mesma
 * regra, o match do router. Módulo puro de propósito: roda no servidor
 * (`cache-config.ts` registra a lista) E no cliente (`routes/$.tsx` filtra o
 * `loaderDeps`).
 *
 * As duas pontas precisam concordar. Com a chave descartando `?utm_source=` e o
 * router mantendo, `/x?utm_source=…` gravava na chave `/x` um HTML cujo estado
 * desidratado era de OUTRA URL: quem chegava em `/x` hidratava sem match, o
 * router recarregava e o React refazia a árvore — React #418 (medido em
 * 21/10/2026 na /sex-shop desktop e reproduzido em PLPs frias).
 */
import { isTrackingParam } from "@decocms/blocks/sdk/urlUtils";
import { blockedQs, EXTRA_BLOCKED_QS, frameworkBlockedQs } from "~/blockedQs";

// O único critério de exclusão é: o parâmetro muda o HTML renderizado no
// servidor? Varredura de `searchParams.get/has` em src/ dá o conjunto funcional —
// fresh-partial, idsku, skuId, q, map, O, page, payload, __cseg, __sku e `t`.
// Desses, `t`, `idsku` e `fresh-partial` aparecem nas listas de bloqueio.
const FUNCTIONAL_QS = new Set([
  // /buscavazia?t=<termo> vira o <title> da página
  // (getTitleFallback em src/components/seo/PageSEO.tsx:492).
  "t",
  // SKU escolhida e partial nav do Fresh, lidos pelo loader do ProductDetails.
  "idsku",
  "fresh-partial",
]);

/** O que a chave de cache descarta além da lista embutida do framework. */
export const CACHE_STRIPPED_QS = [...frameworkBlockedQs, ...blockedQs, ...EXTRA_BLOCKED_QS].filter(
  (qs) => !FUNCTIONAL_QS.has(qs.toLowerCase()),
);

const STRIPPED = new Set(CACHE_STRIPPED_QS.map((qs) => qs.toLowerCase()));

/**
 * Mesmo predicado da chave de cache, sem diferenciar maiúsculas: a lista da
 * loja mais a embutida do framework (`isTrackingParam`). Não depende do
 * `registerTrackingParams` ter rodado, porque no cliente ele não roda.
 */
export const isCacheStrippedParam = (param: string): boolean =>
  STRIPPED.has(param.toLowerCase()) || isTrackingParam(param);
